import React, { useCallback, useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, BoxProps, Button, IconButton, Stack, TextField, Typography } from "@mui/material";

const boxSx: BoxProps["sx"] = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "white",
  borderRadius: "8px",
  minWidth: "500px"
};

interface Props {
  incorrect?: boolean;
  onCancel: () => void;
  onSubmit: (value: string) => void;
}

export const PdfPasswordPrompt: React.FC<Props> = ({ incorrect, onCancel, onSubmit }) => {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, []);

  const handleSubmit = useCallback(() => {
    if (value) onSubmit(value);
  }, [value, onSubmit]);

  return (
    <Box sx={boxSx}>
      <Box sx={{ background: "#fff", borderRadius: "10px 10px 0px 0px", padding: "30px 30px 0 30px" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ borderBottom: "1px solid  #DAE0E2", paddingBottom: "5px" }}
        >
          <span>Password required</span>
          <IconButton onClick={onCancel}>
            <CloseIcon className="icon" />
          </IconButton>
        </Stack>
      </Box>
      <Stack alignItems="stretch" sx={{ p: "20px 30px 30px 30px" }}>
        <Typography variant="body1" color="#0A3142">
          This document is protected. Enter its password to view the file.
        </Typography>
        <TextField
          inputRef={inputRef}
          type="password"
          autoComplete="off"
          label="Password"
          InputLabelProps={{ shrink: true }}
          value={value}
          error={incorrect}
          helperText={incorrect ? "Incorrect password. Please try again." : undefined}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSubmit();
            }
          }}
          sx={{ mt: "20px" }}
        />
        <Stack sx={{ mt: "30px" }} direction="row" justifyContent="flex-end" gap="20px">
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!value} onClick={handleSubmit}>
            Unlock
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
